import { ApolloClient, ApolloLink as ClientApolloLink, DocumentNode, FetchResult, InMemoryCache, Observable } from '@apollo/client';
import { Environment } from '@environment/interfaces';
import { setEnvironmentVariables } from '@environment/utils/initialize-environment';
import { ApolloLink } from 'apollo-link';
import { createAuthLink } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';

const AWS_REGION = 'us-east-1';

export class GraphQLClientService {
  private static instance: GraphQLClientService;

  private client: ApolloClient<unknown>;
  private environment: Environment = setEnvironmentVariables(process.env);

  private constructor() {
    this.initializeClient();
  }

  public static getInstance(): GraphQLClientService {
    if (!GraphQLClientService.instance) {
      GraphQLClientService.instance = new GraphQLClientService();
    }
    return GraphQLClientService.instance;
  }

  subscribe<T = any>(query: DocumentNode, variables: Record<string, unknown>): Observable<FetchResult<T>> {
    return this.client.subscribe({query, variables: { ...variables }}) as Observable<FetchResult<T>>;
  }

  stop(): void {
    this.client.stop();
  }

  private initializeClient() {
    const url = this.getEnvVariable('graphqlApiUrl');
    const apiKey = this.getEnvVariable('graphqlApiKey');

    const authConfig = {
      url,
      region: AWS_REGION,
      auth: {
        type: 'API_KEY' as const,
        apiKey
      }
    };

    const handshakeLink = createSubscriptionHandshakeLink(authConfig);
    const authLink = createAuthLink(authConfig);
    const link = ApolloLink.from([authLink, handshakeLink]);

    this.client = new ApolloClient({
      link: link as unknown as ClientApolloLink,
      cache: new InMemoryCache(),
    });
  }

  private getEnvVariable(name: keyof Environment): string {
    const value: string = this.environment[name] as string;
    if (!value) {
      throw new Error(`${name} not set in .env`);
    }
    return value;
  }
}
